import { colors } from '@kmns-internal/furikake/tokens'
import {
  Box,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { SubmissionRateBar } from 'src/features/monitoringDashboardAlpha/components/SubmissionRateBar/SubmissionRateBar'
import { handleActivateKey } from 'src/features/monitoringDashboardAlpha/handleActivateKey'
import { rowsPerPageOptions } from 'src/features/monitoringDashboardAlpha/hooks/useMonitoringFilters'
import { computeSubmissionRate } from 'src/features/monitoringDashboardAlpha/selector/computeSubmissionRate'
import { toReportStatusGroups } from 'src/features/monitoringDashboardAlpha/selector/toReportStatusGroups'
import {
  PlaceReportSummary,
  ReportStatusGroupKey,
} from 'src/features/monitoringDashboardAlpha/types'

type Props = {
  summaries: PlaceReportSummary[]
  /** 期間内にスケジュールがある全現場数（ページネーションの総数） */
  count: number
  offset: number
  limit: number
  isLoading: boolean
  onChangePage: (offset: number) => void
  onChangeLimit: (limit: number) => void
  /** 件数セル選択時。現場＋ステータスグループ（null=全件）で詳細（L2）へ遷移する */
  onSelectCell: (
    placeNodeUuid: string,
    status: ReportStatusGroupKey | null,
  ) => void
}

/** 0 は「-」表示にする */
const formatCount = (n: number): string => (n > 0 ? `${n}件` : '-')

const headCellSx = {
  fontWeight: 600,
  color: colors.text.secondary,
  whiteSpace: 'nowrap',
} as const

/**
 * 件数セル。
 * onClick が渡され、かつ件数が 1 以上のときだけクリックで詳細へ遷移できる。
 * 各ステータスセル・全件数セルに onClick を渡している（件数 0 のセルは遷移不可）。
 * クリック可能なセルはキーボード（Enter/Space）でも遷移できるようにする。
 */
const CountCell = ({
  count,
  bgcolor,
  onClick,
  display,
  borderLeft,
  recordingCount,
}: {
  count: number
  bgcolor?: string
  /** 指定したセルのみクリック可能になる */
  onClick?: () => void
  /** 表示文字列（省略時は count を「N件 / -」整形） */
  display?: string
  /** 左に区切り線を引くか（全件数列用） */
  borderLeft?: boolean
  /** 「(N件記録中)」として添える内訳件数（>0 のときだけ表示。カードと同表記） */
  recordingCount?: number
}) => {
  const clickable = !!onClick && count > 0
  const hasRecording = recordingCount !== undefined && recordingCount > 0
  return (
    <TableCell
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable && onClick ? handleActivateKey(onClick) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      sx={{
        bgcolor,
        borderLeft: borderLeft ? `1px solid ${colors.divider.main}` : undefined,
        cursor: clickable ? 'pointer' : 'default',
        // クリック可能を示す下線は件数本体のみに付ける（内訳の記録中には付けない）
        '&:hover .count-value': clickable
          ? { textDecoration: 'underline' }
          : undefined,
        '&:focus-visible': clickable
          ? { outline: `2px solid ${colors.primary.main}`, outlineOffset: -2 }
          : undefined,
      }}
    >
      <Stack spacing={0.25}>
        <Typography variant="body2" component="span" className="count-value">
          {display ?? formatCount(count)}
        </Typography>
        {hasRecording && (
          <Typography
            variant="caption"
            noWrap
            sx={{ color: colors.text.secondary }}
          >
            ({recordingCount}件記録中)
          </Typography>
        )}
      </Stack>
    </TableCell>
  )
}

/**
 * 現場ごとの実施状況テーブル（Card 形式・内部にページネーション）
 *
 * PD コメント: Table 部分を独立させた Card 形式にし、内部に Pagination を配置する。
 * 各件数セルをクリックすると、現場＋ステータスで絞り込んだ詳細（L2）へ遷移する。
 */
export const PlaceSummaryTable = ({
  summaries,
  count,
  offset,
  limit,
  isLoading,
  onChangePage,
  onChangeLimit,
  onSelectCell,
}: Props) => {
  const page = limit > 0 ? Math.floor(offset / limit) : 0

  return (
    <Paper variant="outlined">
      <TableContainer sx={{ position: 'relative' }}>
        {isLoading && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}
          />
        )}
        <Table size="small" sx={{ bgcolor: colors.white }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, minWidth: 240 }}>
                実施率
              </TableCell>
              <TableCell sx={headCellSx}>実施済み</TableCell>
              <TableCell sx={headCellSx}>期限切れ未実施</TableCell>
              <TableCell sx={headCellSx}>期間中</TableCell>
              <TableCell sx={headCellSx}>開始前</TableCell>
              <TableCell
                sx={{
                  ...headCellSx,
                  borderLeft: `1px solid ${colors.divider.main}`,
                }}
              >
                全件数
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaries.map(summary => {
              const groups = toReportStatusGroups(summary)
              return (
                <TableRow key={summary.placeNodeUuid}>
                  <TableCell>
                    <Stack spacing={1}>
                      <Typography
                        variant="body2"
                        noWrap
                        title={summary.placeName}
                        sx={{ maxWidth: 280 }}
                      >
                        {summary.placeName}
                      </Typography>
                      <SubmissionRateBar
                        rate={computeSubmissionRate(summary)}
                      />
                    </Stack>
                  </TableCell>
                  {/*
                    各ステータスセル → 現場×ステータスグループで絞り込んだ詳細(L2)へ。
                    全件数セル → 現場のみ(全ステータス)で絞り込んだ詳細(L2)へ。
                    件数が 0（「-」）のセルはクリック不可。
                  */}
                  <CountCell
                    count={groups.submitted}
                    bgcolor={colors.green[0]}
                    onClick={() =>
                      onSelectCell(summary.placeNodeUuid, 'submitted')
                    }
                  />
                  <CountCell
                    count={groups.notImplemented}
                    recordingCount={groups.notImplementedRecording}
                    bgcolor={colors.red[1]}
                    onClick={() =>
                      onSelectCell(summary.placeNodeUuid, 'notImplemented')
                    }
                  />
                  <CountCell
                    count={groups.duringPeriod}
                    recordingCount={groups.duringPeriodRecording}
                    onClick={() =>
                      onSelectCell(summary.placeNodeUuid, 'duringPeriod')
                    }
                  />
                  <CountCell
                    count={groups.notStarted}
                    onClick={() =>
                      onSelectCell(summary.placeNodeUuid, 'notStarted')
                    }
                  />
                  <CountCell
                    count={groups.total}
                    display={`${groups.total}件`}
                    borderLeft
                    onClick={() => onSelectCell(summary.placeNodeUuid, null)}
                  />
                </TableRow>
              )
            })}
            {!isLoading && summaries.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.text.secondary }}
                    >
                      該当する現場がありません
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_, newPage) => onChangePage(newPage * limit)}
        rowsPerPage={limit}
        onRowsPerPageChange={event =>
          onChangeLimit(parseInt(event.target.value, 10))
        }
        rowsPerPageOptions={rowsPerPageOptions}
        labelRowsPerPage="1ページあたり:"
        labelDisplayedRows={({ from, to, count: total }) =>
          `${from}〜${to} / ${total}件`
        }
        sx={{
          '& .MuiTablePagination-selectLabel': { margin: 0 },
          '& .MuiTablePagination-displayedRows': { margin: 0 },
        }}
      />
    </Paper>
  )
}
